import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  Shield, 
  Clock, 
  Trophy, 
  Users, 
  Zap, 
  GamepadIcon,
  Gift,
  CheckCircle,
  XCircle,
  Target,
  Timer,
  Award,
  Smartphone
} from 'lucide-react';
import { faqs } from '../data/mockData';

const WhatIsFokatmain = () => {
  const navigate = useNavigate();

  const handleViewLiveGames = () => {
    navigate('/play-games');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-20 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/5 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
            <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 mb-6 px-6 py-2">
              Skill-Based Gaming Platform
            </Badge>
            
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 break-words">
              What is 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                {' '}Fokatmain?
              </span>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-12 leading-relaxed px-2">
              India's first skill-based micro-gaming platform where you play quick games, 
              compete with real players, and win amazing gadgets—not cash!
            </p>
          </div>
        </div>
      </section>

      {/* Core Concept */}
      <section className="py-20 bg-gradient-to-b from-gray-900/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="animate-slide-in-left">
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 mb-6">
                The Concept
              </Badge>
              
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 break-words">
                Quick Games, Real Skills, Amazing Prizes
              </h2>
              
              <div className="space-y-6 text-lg text-gray-300">
                <p>
                  Fokatmain revolutionizes mobile gaming by combining the thrill of competition 
                  with the excitement of winning real gadgets. Our platform features lightning-fast 
                  games that test your skills, not your luck.
                </p>
                
                <p>
                  Every game takes just 1-2 minutes to complete, costs only ₹10 to enter, and 
                  rewards winners with premium gadgets like iPhones, MacBooks, PlayStation consoles, 
                  and more. No cash prizes, just amazing tech rewards!
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-xl">
                  <div className="text-2xl font-bold text-yellow-400">₹10</div>
                  <div className="text-sm text-gray-400">Entry Fee Per Game</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-green-900/30 to-blue-900/30 rounded-xl">
                  <div className="text-2xl font-bold text-yellow-400">60-120s</div>
                  <div className="text-sm text-gray-400">Average Game Time</div>
                </div>
              </div>
            </div>
            
            {/* Visual */}
            <div className="relative animate-slide-in-right">
              <div className="relative">
                {/* Main illustration */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 shadow-2xl">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Game Types */}
                    {[
                      { icon: Target, title: "Trivia Quiz", desc: "Knowledge & Speed" },
                      { icon: Timer, title: "Spin Wheel", desc: "Perfect Timing" },
                      { icon: Zap, title: "Tap Challenge", desc: "Reflexes & Accuracy" },
                      { icon: Award, title: "Memory Game", desc: "Focus & Recall" }
                    ].map((game, index) => {
                      const IconComponent = game.icon;
                      return (
                        <div key={index} className="text-center p-4 bg-blue-900/30 rounded-xl">
                          <IconComponent className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                          <div className="text-white font-semibold text-sm">{game.title}</div>
                          <div className="text-gray-400 text-xs">{game.desc}</div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-6 text-center">
                    <div className="text-yellow-400 font-bold text-lg">Win Real Gadgets!</div>
                    <div className="text-gray-400 text-sm">iPhone • MacBook • PlayStation • AirPods</div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 bg-green-400 text-black px-3 py-2 rounded-full text-sm font-bold animate-bounce">
                  Skill-Based
                </div>
                <div className="absolute -bottom-4 -left-4 bg-yellow-400 text-black px-3 py-2 rounded-full text-sm font-bold animate-pulse">
                  No Gambling
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gradient-to-b from-transparent to-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-6">
              Platform Features
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 break-words">
              Why Choose Fokatmain?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "100% Skill-Based",
                description: "Winners determined by skill, speed, and accuracy - never by luck or chance.",
                color: "green"
              },
              {
                icon: Clock,
                title: "Lightning Fast Games",
                description: "Complete games in 60-120 seconds. Perfect for busy lifestyles and quick entertainment.",
                color: "blue"
              },
              {
                icon: Gift,
                title: "Real Gadget Prizes",
                description: "Win premium gadgets like iPhones, MacBooks, gaming consoles, and latest tech.",
                color: "yellow"
              },
              {
                icon: Trophy,
                title: "Fair Competition",
                description: "Advanced anti-cheat systems ensure every player competes on a level playing field.",
                color: "purple"
              },
              {
                icon: Zap,
                title: "Instant Entry",
                description: "Pay ₹10 via UPI/Paytm/Razorpay and start playing immediately. No lengthy processes.",
                color: "orange"
              },
              {
                icon: Users,
                title: "Transparent Leaderboards",
                description: "Real-time scoring, live leaderboards, and transparent winner selection process.",
                color: "pink"
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              const colorClasses = {
                green: "text-green-400 bg-green-400/20",
                blue: "text-blue-400 bg-blue-400/20", 
                yellow: "text-yellow-400 bg-yellow-400/20",
                purple: "text-purple-400 bg-purple-400/20",
                orange: "text-orange-400 bg-orange-400/20",
                pink: "text-pink-400 bg-pink-400/20"
              };
              
              return (
                <Card key={index} className="gaming-card p-6 text-center animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <CardHeader className="pb-4">
                    <div className={`mx-auto mb-4 p-4 rounded-full w-16 h-16 flex items-center justify-center ${colorClasses[feature.color]}`}>
                      <IconComponent className={`w-8 h-8 ${colorClasses[feature.color].split(' ')[0]}`} />
                    </div>
                    <CardTitle className="text-xl text-white">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gaming vs Gambling */}
      <section className="py-20 bg-gradient-to-r from-red-900/10 to-green-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mb-6">
              Important Distinction
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 break-words">
              Skill Gaming vs Gambling: The Difference
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Gambling */}
            <Card className="bg-gradient-to-br from-red-900/20 to-red-800/20 border-red-500/30">
              <CardHeader className="text-center pb-6">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <CardTitle className="text-2xl text-red-400">Traditional Gambling</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3 text-gray-300">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>Outcome based on pure luck</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>Cash prizes and monetary rewards</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>House always has an edge</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>Can lead to addiction and losses</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>Regulated or banned in many places</span>
                </div>
              </CardContent>
            </Card>
            
            {/* Skill Gaming */}
            <Card className="bg-gradient-to-br from-green-900/20 to-green-800/20 border-green-500/30">
              <CardHeader className="text-center pb-6">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <CardTitle className="text-2xl text-green-400">Fokatmain Skill Gaming</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Winners decided by skill and performance</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Gadget prizes only, no cash payouts</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Fair competition with transparent rules</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Promotes skill development and fun</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-300">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Legal and compliant platform</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900/50 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 mb-6">
              Got Questions?
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 break-words">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400">
              Everything you need to know about Fokatmain
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`}
                className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-lg px-6 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <AccordionTrigger className="text-left text-white hover:text-yellow-400 font-semibold py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 break-words">
              Ready to Test Your Skills?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 mb-8 leading-relaxed px-2">
              Join thousands of players competing for amazing gadgets every day.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleViewLiveGames}
                className="btn-primary px-8 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300"
              >
                View Live Games
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatIsFokatmain;